/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useFlexLayout,
  TableState,
  Column,
  ColumnInstance,
  UsePaginationInstanceProps,
  UseSortByInstanceProps,
  UseSortByColumnProps,
  UsePaginationState,
  UseSortByState,
  UseTableInstanceProps,
} from 'react-table';
import Utils from './model/Utils';
import MonsterOpen5e from './model/MonsterOpen5e';
import DataModel from './model/DataModel';
import PropertySet from './PropertySet';
import Open5e from './model/Open5e';

interface MonsterGridProps {
  onSelectMonster?: (slug: string) => void;
  minCr: number | null;
  maxCr: number | null;
  keywords: string;
  selectedEnvironments: string[];
  pageSize: number | null;
  currentPage: number | null;
  sortBy: { id: string; desc: boolean }[];
  dataModel: DataModel;
  onUpdateMinCr: (minCr: number) => void;
  onUpdateMaxCr: (maxCr: number) => void;
  onUpdateKeywords: (keywords: string) => void;
  onUpdateSelectedEnvironments: (environments: string[]) => void;
  onUpdatePageSize: (pageSize: number) => void;
  onUpdateCurrentPage: (currentPage: number) => void;
  onUpdateSort: (sortBy: { id: string; desc: boolean }[]) => void;
}

const MonsterGrid: React.FC<MonsterGridProps> = ({
  onSelectMonster, minCr, maxCr, keywords, selectedEnvironments, pageSize, currentPage, sortBy, dataModel,
  onUpdateMinCr, onUpdateMaxCr, onUpdateKeywords, onUpdateSelectedEnvironments, onUpdatePageSize, onUpdateCurrentPage, onUpdateSort
}) => {
  const [monsterData, setMonsterData] = useState<MonsterOpen5e[]>([]);
  const [filteredMonsterData, setFilteredMonsterData] = useState<MonsterOpen5e[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadProgress, setLoadProgress] = useState<string>('Retrieving other data.');
  const [internalPageSize, setInternalPageSize] = useState<number>(pageSize || 25);
  const [environments, setEnvironments] = useState<string[]>(['Water', 'Mountains', 'Desert', 'Hills']);
  const [envOpen, setEnvOpen] = useState<boolean>(false);

  const containsAllSearchWords = (obj: any, searchWords: string[]): boolean => {
    const objValues = Object.values(obj);
    return searchWords.every(word =>
      objValues.some(value => {
        if (typeof value === 'object' && value !== null) {
          return containsAllSearchWords(value, [word]);  // Recursive call for nested objects
        }
        if (value && typeof value === 'string' && value.toLowerCase().includes(word.toLowerCase())) {
          return true;
        }
        return false;
      })
    );
  };

  const containsAnySearchWords = (obj: any, searchWords: string[]): boolean => {
    const objValues = Object.values(obj);
    return searchWords.some(word =>
      objValues.some(value => {
        if (typeof value === 'object' && value !== null) {
          return containsAnySearchWords(value, [word]);  // Recursive call for nested objects
        }
        if (value && typeof value === 'string' && value.toLowerCase().includes(word.toLowerCase())) {
          return true;
        }
        return false;
      })
    );
  };

  const containsSearchCriteria = (obj: any, positiveWords: string[], negativeWords: string[]): boolean => {
    return containsAllSearchWords(obj, positiveWords) && !containsAnySearchWords(obj, negativeWords);
  };

  const parseSearchString = (searchString: string) => {
    const regex = /-?"[^"]*"|-?'[^']*'|\S+/g;
    let match;
    const positive: string[] = [];
    const negative: string[] = [];

    while ((match = regex.exec(searchString)) !== null) {
      const term = match[0];
      if ((term.startsWith('-"') && term.endsWith('"')) || (term.startsWith('-\'') && term.endsWith('\''))) {
        negative.push(term.slice(2, -1).toLowerCase());
      } else if ((term.startsWith('"') && term.endsWith('"')) || (term.startsWith('\'') && term.endsWith('\''))) {
        positive.push(term.slice(1, -1).toLowerCase());
      } else if (term.startsWith('-')) {
        negative.push(term.slice(1).toLowerCase());
      } else {
        positive.push(term.toLowerCase());
      }
    }

    return { positive, negative };
  };

  useEffect(() => {
    const allEnvs = monsterData.flatMap(mon => mon.environments || []);
    const uniqueEnvs = Open5e.cleanupEnvironments(Array.from(new Set(allEnvs)));
    setEnvironments(uniqueEnvs);
  }, [monsterData]);

  useEffect(() => {
    let reducedMonsters = monsterData;
    if (minCr && !isNaN(minCr)) {
      reducedMonsters = reducedMonsters.filter(mon => mon.cr >= minCr);
    }
    if (maxCr && !isNaN(maxCr)) {
      reducedMonsters = reducedMonsters.filter(mon => mon.cr <= maxCr);
    }
    if (keywords !== '') {
      const { positive: positiveWords, negative: negativeWords } = parseSearchString(keywords.trim().toLowerCase());
      reducedMonsters = reducedMonsters.filter(mon => containsSearchCriteria(mon, positiveWords, negativeWords));
    }
    const selectedEnvs = selectedEnvironments.filter(env => env && env != '--')
    if (selectedEnvs.length > 0) {
      reducedMonsters = reducedMonsters.filter(row => {
        for (const env of selectedEnvs ) {
          if (row.environments.includes(env))
            return true;
        }
        return false;
      });
    }

    const count = reducedMonsters.length;
    let newLastPage: number = Math.floor(count / internalPageSize) + 1;
    if ((currentPage ?? 0) >= newLastPage) {
      onUpdateCurrentPage(newLastPage - 1);
    }

    setFilteredMonsterData(reducedMonsters);
  }, [minCr, maxCr, keywords, selectedEnvironments, monsterData]);

  useEffect(() => {
    const fetchMonsters = async () => {
      if (dataModel?.monsters == null || dataModel.monsters.data.length === 0 || dataModel.monsters.progress) {
        if (dataModel.monsters.progress) {
          setLoadProgress(dataModel.monsters.progress);
        }
        setLoading(true);
      } else {
        setLoadProgress('');
        setLoading(false);
      }
      const newMonsters = [];
      for (const monster of dataModel?.monsters?.data || []) {
        const copy = structuredClone(monster);
        copy.environments = Open5e.cleanupEnvironments(monster.environments);
        newMonsters.push(copy);
      }
      setMonsterData(newMonsters);
      setFilteredMonsterData(newMonsters);
    };

    fetchMonsters();
  }, [dataModel, dataModel.monsters, dataModel.monsters.data, dataModel.monsters.progress]);

  useEffect(() => {
    if (pageSize !== null) {
      setInternalPageSize(pageSize);
    }
  }, [pageSize]);

  useEffect(() => {
    onUpdatePageSize(internalPageSize);
  }, [internalPageSize]);

  const crOptions = [
    { value: 0, label: "0" },
    { value: 0.125, label: "1/8" },
    { value: 0.25, label: "1/4" },
    { value: 0.5, label: "1/2" },
    ...Array.from({ length: 31 }, (v, i) => ({ value: i, label: String(i) }))
  ];

  const columns: Column<MonsterOpen5e>[] = React.useMemo(() => [
    {
      width: 40, Header: 'CR', accessor: 'cr',
      sortType: (rowA: { original: { cr: any; }; }, rowB: { original: { cr: any; }; }) => {
        const crA = rowA.original.cr;
        const crB = rowB.original.cr;
        return crA > crB ? 1 : crA < crB ? -1 : 0;
      },
      Cell: ({value}) => {return <div style={{textAlign: 'right', paddingRight: '15px'}}>{value}</div>}
    },
    { width: 200, Header: 'Name', accessor: 'name' },
    { width: 70, Header: 'Source', accessor: 'document__slug' },
    { width: 100, Header: 'Type', accessor: 'type' },
    {
      width: 100, Header: 'Size', accessor: 'size',
      sortType: (rowA: { values: { size: string; }; },
                 rowB: { values: { size: string; }; }) => Utils.sortSizes(rowA.values.size, rowB.values.size)
    },
    { width: 150, Header: 'Alignment', accessor: 'alignment',
      sortType: (rowA: any, rowB: any, columnId: number) => {
        const a = rowA.values[columnId].toLowerCase();
        const b = rowB.values[columnId].toLowerCase();
        return a.localeCompare(b);
      },
      Cell: ({ value }) => {
        if (typeof value === 'string') {
          // Capitalize the first letter of each word
          value = value.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
        }
        return <span>{value}</span>;
      },
    },
    { width: 250, Header: 'Environments', accessor: 'environments',
      sortType: (rowA: any, rowB: any, columnId: number) => {
        const prep = (val: any) => {
          if (Array.isArray(val)) {
            return val.map((v: string) => v.toLowerCase()).sort().join(', ');
          }
          return val ? String(val).toLowerCase() : '';
        }
        const a = prep(rowA.values[columnId]);
        const b = prep(rowB.values[columnId]);
        return a.localeCompare(b);
      },
      Cell: ({ value }) => {
        if (Array.isArray(value)) {
          const sortedValue = value.sort((a:any, b:any) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          return <span>{sortedValue.join(', ')}</span>;
        }
        return value;
      },
    },
    { width: 40, Header: 'HP', accessor: 'hit_points',
      Cell: ({value}) => {return <div style={{textAlign: 'right', paddingRight: '15px'}}>{value}</div>} },
    { width: 40, Header: 'AC', accessor: 'armor_class' },
  ], []);

  const initialState: Partial<TableState<MonsterOpen5e>> & Partial<UsePaginationState<MonsterOpen5e>> & Partial<UseSortByState<MonsterOpen5e>> = {
    pageIndex: currentPage ?? 0,  // From UsePaginationState
    pageSize: internalPageSize,  // From UsePaginationState
    sortBy: sortBy || [{ id: 'name', desc: false }],  // From UseSortByState
  };

  // Define the correct type for the table instance
  type TableInstanceWithPlugins<T extends object> = UseTableInstanceProps<T> &
    UsePaginationInstanceProps<T> &
    UseSortByInstanceProps<T>;

  type SortableColumnInstance<T extends object> = ColumnInstance<T> & UseSortByColumnProps<T>;
  type TableStateWithPagination<T extends object> = TableState<T> & UsePaginationState<T>;

  const tableInstance: TableInstanceWithPlugins<MonsterOpen5e> = useTable<MonsterOpen5e>(
    { columns, data: filteredMonsterData, initialState },
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout
  ) as unknown as TableInstanceWithPlugins<MonsterOpen5e>;
  
  // Destructure state from the table instance
// Cast state to include the sortBy property
const { state: tableState } = tableInstance as TableInstanceWithPlugins<MonsterOpen5e> & {
  state: TableState<MonsterOpen5e> & {
    sortBy: Array<{ id: string; desc: boolean }>;
  };
};

useEffect(() => {
  if (tableState.sortBy && onUpdateSort) {
    onUpdateSort(tableState.sortBy);
  }
}, [tableState.sortBy, onUpdateSort]);
    
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,            // Comes from usePagination
    nextPage,        // Comes from usePagination
    previousPage,    // Comes from usePagination
    canNextPage,     // Comes from usePagination
    canPreviousPage, // Comes from usePagination
    pageOptions,     // Comes from usePagination
    state,           // General state from useTable
    setPageSize,     // Comes from usePagination
    gotoPage,        // Comes from usePagination
  } = tableInstance;

  const paginationState = state as TableStateWithPagination<MonsterOpen5e>;

  useEffect(() => {
    if (currentPage !== null) {
      gotoPage(currentPage);  // Use gotoPage to set the current page
    }
  }, [currentPage]);

  useEffect(() => {
    onUpdateCurrentPage(paginationState.pageIndex);
  }, [paginationState.pageIndex]);

  useEffect(() => {
    onUpdatePageSize(internalPageSize);
  }, [internalPageSize]);

  const handleNameClick = (event: React.MouseEvent<HTMLAnchorElement>, cell: any) => {
    event.preventDefault();
    if (onSelectMonster)
      onSelectMonster(cell.row.original.slug);
  };

  const handleCrChange = (value: string, isMin: boolean) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      isMin ? onUpdateMinCr(num) : onUpdateMaxCr(num);
    } else {
      isMin ? onUpdateMinCr(0) : onUpdateMaxCr(0);
    }
  };

  const placeholder = "must match all words, use '-' to exclude words, use \" or ' to surround exact phrases";
  return (
    <div style={{cursor: 'auto'}} id='MonsterGrid'>
      <table>
        <tbody>
          <tr>
            <th colSpan={3} style={{paddingLeft: '10px', paddingRight: '10px'}}>CR range</th>
            <td rowSpan={2} style={{borderLeft: '1px solid grey', paddingLeft: '10px'}}>
              &nbsp;Keywords: <input value={keywords} placeholder={placeholder} onChange={e => onUpdateKeywords(e.target.value)} style={{ width: `500px` }} />
              <PropertySet className='environments' columns={4} isOpen={envOpen} setIsOpen={setEnvOpen} alwaysShow={true}
                           name="Environments" options={environments} values={selectedEnvironments} onSave={(vals) => onUpdateSelectedEnvironments(vals.split(/, ?/))}
                           onClickOutside={() => setEnvOpen(false)} showClear={true}/>
            </td>
          </tr>
          <tr>
            <td style={{paddingLeft: '10px'}}><input value={minCr || ''} placeholder='min CR' onChange={e => handleCrChange(e.target.value, true)} style={{ width: `50px` }} /></td>
            <td>-</td>
            <td style={{paddingRight: '10px'}}><input value={maxCr || ''} placeholder='max CR' onChange={e => handleCrChange(e.target.value, false)} style={{ width: `50px` }} /></td>
          </tr>
        </tbody>
      </table>

      <table {...getTableProps()} style={{ width: '100%', background: 'white' }}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                const sortableColumn = column as unknown as SortableColumnInstance<MonsterOpen5e>;
                return (
                  <th {...sortableColumn.getHeaderProps(sortableColumn.getSortByToggleProps())}
                    style={{ width: `${sortableColumn.width}px`, textWrap: 'nowrap', overflow: 'hidden', cursor: 'pointer', borderBottom: '1px solid black'}}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <div style={{ flex: 1, textAlign: 'center', marginRight: 'auto', marginLeft: 'auto', width: '100%' }}>
                        {sortableColumn.render('Header')}
                      </div>
                      <span style={{ width: '22px' }}>
                        {sortableColumn.isSorted ? (sortableColumn.isSortedDesc ? '🔽' : '🔼') : ''}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}
                    style={{ width: `${cell.column.width}px`, textWrap: 'nowrap', overflow: 'hidden' }} >
                    {cell.column.Header === 'Name' ? <a href='#' onClick={(event) => handleNameClick(event, cell)}>{cell.render('Cell')}</a> : cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div> &nbsp; &nbsp; &nbsp;
        <button onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
        <button onClick={nextPage} disabled={!canNextPage}>Next</button> &nbsp; &nbsp;

        <span>
          Page {paginationState.pageIndex + 1} of {pageOptions.length} &nbsp; &nbsp;
        </span>
        <select
          value={internalPageSize}
          onChange={e => {
            const newSize = Number(e.target.value);
            setInternalPageSize(newSize);
            setPageSize(newSize);
          }}
        >
          {[15, 25, 50, 100].map(size => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select> items per page
        <span style={{display: 'block', textAlign: 'right', marginTop: '-15px'}}>
          {loading && <><b style={{color: 'red'}}>Loading...</b> &nbsp; {loadProgress}</>}
          {!loading && monsterData.length !== filteredMonsterData.length && <span>Filtered to {filteredMonsterData.length} out of {monsterData.length} total monsters.</span>}
          {!loading && monsterData.length === filteredMonsterData.length && <span>{monsterData.length} total monsters.</span>}
        </span>
      </div>
    </div>
  );
};

export default MonsterGrid;
